// The spike's kernel game (gate A1.4): the composition's manifest, its typed event map, a service read through its
// stub, and the error policy. In play a throwing system is isolated (logged once, switched off, the rest keep
// stepping); under the harness (Playwright sets navigator.webdriver) the same throw fails fast, so the spec fails.
// `?errors=isolate|fail-fast` overrides the default.
//
// This is the spike's one kernel game (A2 joined the kernel stream's game and the sim's): `createCore(scene)` builds
// it once with the policy and the services, holding the core module, the scene's modules (the A1 lab's pusher and
// wind, or the valley's controller) with their physics step, and the planted thrower when it is switched on.
import {
  createGame,
  defineService,
  eventNames,
  type CompositionManifest,
  type ContentPack,
  type Game,
  type GameModule,
  type SystemErrorPolicy,
  type SystemStatus,
  type ServiceInfo,
} from "@rpg/kernel";
import type { CombatEvents } from "@rpg/combat";
import type { StandardEvents } from "@rpg/contracts";

/** The standard events (@rpg/contracts, decision 0078) the game uses: their names and payloads are the contract's.
 *  Spike 002's own genre-neutral events became the standard set; `impact` is new, emitted by the core's adapter from
 *  combat's hits. */
export type StandardGameEvents = Pick<
  StandardEvents,
  | "impact"
  | "world:chunk"
  | "sky:time"
  | "sky:weather"
  | "progress:xp"
  | "progress:level"
  | "item:gained"
  | "item:spent"
  | "audio:cue"
  | "dialogue:open"
  | "dialogue:line"
  | "dialogue:close"
  | "story:scene"
  | "story:scene-done"
  | "world:discovered"
  | "story:quest"
  | "meta:unlocked"
  | "meta:found"
  | "interact:request"
  | "interact:done"
>;

/** The game's own event families, as `checkEventNames` reads them (tools/contracts.test.ts): everything not standard. */
export const OWN_EVENTS = ["core:*", "controller:*", "combat:*", "npc:interact"] as const;

/** The game's events: name → payload. Emitting or listening to anything else is a type error. The standard ones come
 *  from @rpg/contracts (`StandardGameEvents`); the rest are the game's own. */
export interface GameEvents extends StandardGameEvents {
  /** Once per sim step, after the clock moves. */
  "core:step": { step: number; time: number };
  /** Once per sim second. */
  "core:second": { seconds: number };
  /** Control changed hands (the player mounted, or got control back): the camera follows `profile`. */
  "controller:camera": { profile: string; owner: string };
  /** Combat (phase B's seam 1, @rpg/combat): one resolved contact; a fighter's health reached 0; a telegraphed move's
   *  wind-up started; a move's (or the dodge's or block's) phase changed. Entity ids are kernel entities. */
  "combat:hit": CombatEvents["combat:hit"];
  "combat:defeated": CombatEvents["combat:defeated"];
  "combat:telegraph": CombatEvents["combat:telegraph"];
  "combat:move": CombatEvents["combat:move"];
  // world:chunk (phase C, seam 1), sky:time and sky:weather (seam 4), progress:* and item:* (D1, the rpg stream),
  // audio:cue and dialogue:* (seam 1), story:scene and story:scene-done (the seams' item 1), world:discovered (D2's
  // map), story:quest (D's story) and meta:* (E1) are standard events now: StandardGameEvents above.
  /** Phase D (the town's, an addition to seam 1): the player used an NPC through the interaction system; story's
   *  dialogue opens on it. */
  "npc:interact": { npc: string };
}

// One set of combat event types: @rpg/combat's (the combat and feedback streams both typed them; combat's carry
// `crit` and `style` besides the seam's fields). The names below are the feedback stream's, kept as aliases.
/** A point or direction in an event: `[x, y, z]` metres. */
export type Vec3 = CombatEvents["combat:hit"]["point"];
/** The kinds of hit the impact cue table is keyed by. */
export type HitKind = CombatEvents["combat:hit"]["kind"];
/** `combat:hit`'s payload. */
export type CombatHit = CombatEvents["combat:hit"];
/** `combat:telegraph`'s payload. */
export type CombatTelegraph = CombatEvents["combat:telegraph"];

/** Every event name, for the harness (the list must match the map: a type error otherwise). */
export const EVENT_NAMES = eventNames<GameEvents>()("core:step", "core:second", "controller:camera", "combat:hit", "combat:defeated", "combat:telegraph", "combat:move", "world:chunk", "sky:time", "sky:weather", "progress:xp", "progress:level", "item:gained", "item:spent", "audio:cue", "dialogue:open", "dialogue:line", "dialogue:close", "story:scene", "story:scene-done", "npc:interact", "world:discovered", "story:quest", "meta:unlocked", "meta:found", "impact", "interact:request", "interact:done");

/** A sim-side telemetry service. The real sink (phase E, decision 0064) lives outside the sim in `src/telemetry/` and
 *  listens to the kernel's events; this stub stays as the kernel's one service with a stub, which the kernel spec's
 *  service check reads (it counts sim seconds and sends nothing). */
export interface TelemetrySink {
  send(event: string, data?: Record<string, unknown>): void;
  /** Events taken, for the handle. */
  count(): number;
  readonly isStub?: boolean;
}

/** The telemetry service key, with its stub: counts events, sends nothing. */
export const TELEMETRY = defineService<TelemetrySink>("telemetry", () => {
  let n = 0;
  return { send: () => void n++, count: () => n, isStub: true };
});

/** Planted defects for the kernel checks (`?faults=throwing-system`, `?faults=no-isolation`). */
export const CORE_FAULTS = {
  /** Plant a system that throws on its 60th step (0.5 s at 120 Hz). */
  throwingSystem: false,
  /** Remove the kernel's isolation guard (`Game.faults.noIsolation`): the isolate spec must go red. */
  noIsolation: false,
};

/** The step the planted thrower throws on. */
export const THROW_ON_STEP = 60;

/** The adapter from combat's events to the standard `impact` (rule 5: a payload that differs gets an adapter), so impact
 *  feedback can listen for impacts instead of importing combat. One `impact` per `combat:hit` (its outcome kept, so a
 *  listener can stay silent on blocked hits as the feedback director does) and one "finisher" per `combat:defeated`. */
export function impactFromHit(h: CombatEvents["combat:hit"]): StandardEvents["impact"] {
  return { source: h.attacker, target: h.target, point: [...h.point], dir: [...h.dir], severity: h.severity, kind: h.kind, outcome: h.outcome };
}
/** `combat:defeated` as an `impact` of kind "finisher". */
export function impactFromDefeat(d: CombatEvents["combat:defeated"]): StandardEvents["impact"] {
  return { source: d.by, target: d.entity, point: [...d.point], dir: [...d.dir], severity: d.severity, kind: "finisher" };
}

const coreModule: GameModule<GameEvents> = {
  id: "core",
  version: "0.1.0",
  // checked declarations (decision 0077): the clock's events and the impact adapter's output
  provides: { events: ["core:step", "core:second", "impact"] },
  setup(ctx) {
    ctx.events.on("combat:hit", (h) => ctx.events.emit("impact", impactFromHit(h)));
    ctx.events.on("combat:defeated", (d) => ctx.events.emit("impact", impactFromDefeat(d)));
    let step = 0;
    let time = 0;
    const telemetry = ctx.getService(TELEMETRY);
    ctx.registerSystem(
      ({ dt, events }) => {
        step++;
        const before = Math.floor(time);
        time += dt;
        events.emit("core:step", { step, time });
        if (Math.floor(time) > before) events.emit("core:second", { seconds: Math.floor(time) });
      },
      { name: "core:clock", order: -100 },
    );
    ctx.events.on("core:second", ({ seconds }) => telemetry.send("sim-second", { seconds }));
    ctx.registerSystem(() => {}, { name: "core:idle", order: 100 });
  },
};

const plantedModule: GameModule<GameEvents> = {
  id: "planted",
  version: "0.1.0",
  setup(ctx) {
    let calls = 0;
    ctx.registerSystem(
      () => {
        if (++calls === THROW_ON_STEP) throw new Error("planted fault: throwing-system");
      },
      { name: "planted:thrower" },
    );
  },
};

/** A scene's share of the one game: its modules (registered in this order, after the core's) and the content pack
 *  that builds its world. */
export interface SceneParts {
  modules: readonly GameModule<GameEvents>[];
  content: ContentPack<GameEvents>;
}

function manifest(scene: SceneParts): CompositionManifest {
  return {
    meta: { id: "green-valley", displayName: "Green Valley (spike 002)", version: "0.1.0" },
    modules: [
      { id: "core", version: "0.1.0" },
      ...scene.modules.map((m) => ({ id: m.id, version: m.version })),
      ...(CORE_FAULTS.throwingSystem ? [{ id: "planted", version: "0.1.0" }] : []),
    ],
    contentPacks: [{ id: scene.content.meta.id, version: scene.content.meta.version }],
    // the game's own names: the kernel's lists are open (gate A1.4)
    camera: "third-person-orbit",
    input: { scheme: "action-touch" },
  };
}

const CONTENT: ContentPack<GameEvents> = {
  meta: { id: "green-valley", displayName: "Green Valley", version: "0.1.0" },
  requiresModules: [{ id: "core", versionRange: "^0.1.0" }],
  build: () => {},
};

/** The error policy: `?errors=` wins; else fail-fast under automation (the harness), isolate in play. */
export function errorPolicy(search = typeof location !== "undefined" ? location.search : ""): SystemErrorPolicy {
  const p = new URLSearchParams(search).get("errors");
  if (p === "isolate" || p === "fail-fast") return p;
  return typeof navigator !== "undefined" && navigator.webdriver ? "fail-fast" : "isolate";
}

/** What the handle's `kernel()` returns. */
export interface CoreReport {
  errorPolicy: SystemErrorPolicy;
  systems: SystemStatus[];
  disabled: string[];
  services: ServiceInfo[];
  telemetryEvents: number;
  camera: string;
  input: string;
  events: readonly string[];
  /** The kernel's declaration check at boot (decision 0077): its problems and how many modules declared. */
  declarations: { issues: string[]; declared: number };
}

/** Build the spike's one kernel game, with a scene's modules and content (none: the core alone). */
export function createCore(scene: SceneParts = { modules: [], content: CONTENT }): { game: Game<GameEvents>; report(): CoreReport } {
  const registry: Record<string, GameModule<GameEvents>> = { core: coreModule, planted: plantedModule };
  for (const m of scene.modules) registry[m.id] = m;
  const game = createGame<GameEvents>({ manifest: manifest(scene), moduleRegistry: registry, content: scene.content, errorPolicy: errorPolicy() });
  game.faults.noIsolation = CORE_FAULTS.noIsolation;
  return {
    game,
    report: () => ({
      errorPolicy: game.errorPolicy,
      systems: game.systemStatus(),
      disabled: game.disabledSystems(),
      services: game.services(),
      telemetryEvents: game.getService(TELEMETRY).count(),
      camera: game.manifest.camera,
      input: game.manifest.input.scheme,
      events: EVENT_NAMES,
      declarations: { issues: game.declarations().issues.map((i) => i.message), declared: game.declarations().declared },
    }),
  };
}
